import { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { waysApi, WaysError, type WaysExtraRule, type WaysPolicy, type WaysPolicyKey } from '../../services/waysApi';

/** The four practice rules the hsinc plugin applies to agent sessions, and the modes each takes
 * (ways-api workpolicy.MODES_OF). planGate is ways-api 0.29.4 (hs-pmo/ep19#5). */
const KEYS: { key: WaysPolicyKey; label: string; hint: string; modes: string[] }[] = [
  { key: 'pmoGate', label: 'PMO-first', hint: 'Plan work as a PMO item and check it out before editing.', modes: ['recommend', 'enforce'] },
  { key: 'waysRouting', label: 'Ways routing', hint: 'Messages to other sessions go through Ways, tied to an item.', modes: ['recommend', 'enforce'] },
  { key: 'keepGoing', label: 'Keep going', hint: 'Take the next open item in the epic instead of stopping to ask.', modes: ['off', 'recommend', 'enforce'] },
  { key: 'planGate', label: 'Planning', hint: 'Items are planned (WSJF, criteria, done-when, points) before they are built.', modes: ['off', 'recommend', 'enforce'] },
];

// GET /api/policy answers for a project; one with no overrides answers with the defaults.
const DEFAULTS = '__default__';

function refusal(e: unknown): string {
  if (e instanceof WaysError && e.status === 403) return `${e.message} (needs Ways admin)`;
  return e instanceof Error ? e.message : String(e);
}

/**
 * Practice policy (hs-pmo/ep19#5): the default for every project and this project's own setting,
 * side by side. Changing either is a Ways admin's act, from a person's session; Ways refuses
 * anyone else and the refusal is shown as it comes back.
 */
export default function WaysPracticePolicy({ projectId }: { projectId: string }) {
  const [def, setDef] = useState<WaysPolicy | null>(null);
  const [mine, setMine] = useState<WaysPolicy | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const [d, p] = await Promise.all([waysApi.policy(DEFAULTS), waysApi.policy(projectId)]);
      setDef(d);
      setMine(p);
      setError(null);
    } catch (e) { setError(refusal(e)); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const set = async (scope: 'default' | 'project', key: WaysPolicyKey, value: string) => {
    setBusy(`${scope}:${key}`);
    setError(null);
    try {
      await waysApi.setPolicy(scope === 'project' ? { project: projectId, [key]: value } : { [key]: value });
      await load();
    } catch (e) { setError(refusal(e)); } finally { setBusy(''); }
  };

  const select = (scope: 'default' | 'project', key: WaysPolicyKey, modes: string[], value?: string) => (
    <select value={value || ''} disabled={!!busy} onChange={(e) => set(scope, key, e.target.value)}
      className="px-2 py-1 text-sm rounded border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100">
      {modes.map((m) => <option key={m} value={m}>{m}</option>)}
    </select>
  );

  return (
    <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Practice policy for agents</h3>
      {error ? <p className="mb-2 text-sm text-red-700 dark:text-red-300">{error}</p> : null}
      {!def || !mine ? (error ? null : <Loader2 className="w-4 h-4 animate-spin text-gray-400" />) : (
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-gray-500">
            <tr>
              <th className="py-1 pr-3 font-medium">Rule</th>
              <th className="py-1 pr-3 font-medium">All projects</th>
              <th className="py-1 font-medium">This project</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {KEYS.map(({ key, label, hint, modes }) => (
              <tr key={key}>
                <td className="py-1.5 pr-3">
                  <div className="text-gray-800 dark:text-gray-100">{label}</div>
                  <div className="text-[11px] text-gray-500">{hint}</div>
                </td>
                <td className="py-1.5 pr-3">{select('default', key, modes, def[key])}</td>
                <td className="py-1.5">
                  {select('project', key, modes, mine[key])}
                  {mine[key] !== def[key] ? <span className="ml-1 text-[11px] text-amber-600">overrides</span> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {def && mine ? <PolicyWording projectId={projectId} def={def} mine={mine} onSaved={load} onError={setError} /> : null}
      {mine?.setBy ? (
        <p className="mt-2 text-[11px] text-gray-500">This project last set by {mine.setBy}{mine.setAt ? ` at ${new Date(mine.setAt).toLocaleString()}` : ''}.</p>
      ) : null}
    </section>
  );
}


/**
 * The rules' wording and extra rules (hs-pmo/ep19#10, Zane: "rules live in Ways"), so rewording or
 * adding a rule needs no plugin release. Edited for all projects or for this one; a project's
 * wording overrides key by key, its extra list replaces the default's. Ways admins only.
 */
function PolicyWording({ projectId, def, mine, onSaved, onError }: {
  projectId: string; def: WaysPolicy; mine: WaysPolicy; onSaved: () => void; onError: (e: string) => void;
}) {
  const [scope, setScope] = useState<'default' | 'project'>('default');
  const src = scope === 'default' ? def : mine;
  const [texts, setTexts] = useState<Record<string, string>>({});
  const [extra, setExtra] = useState<WaysExtraRule[]>([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTexts(Object.fromEntries(KEYS.map(({ key }) => [key, src.rules?.[key]?.text || ''])));
    setExtra((src.extra || []).map((x) => ({ ...x })));
  }, [src]);

  const save = async () => {
    setSaving(true);
    try {
      const rules: Partial<Record<WaysPolicyKey, { text: string } | null>> = {};
      KEYS.forEach(({ key }) => {
        const t = (texts[key] || '').trim();
        if (t !== (src.rules?.[key]?.text || '')) rules[key] = t ? { text: t } : null;
      });
      const clean = extra.filter((x) => x.text.trim()).map((x, i) => ({ ...x, id: x.id || `rule-${i + 1}`, text: x.text.trim() }));
      await waysApi.setPolicy({ ...(scope === 'project' ? { project: projectId } : {}),
        ...(Object.keys(rules).length ? { rules } : {}), extra: clean });
      onSaved();
    } catch (e) { onError(refusal(e)); } finally { setSaving(false); }
  };

  const field = 'w-full px-2 py-1 text-sm rounded border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100';
  return (
    <div className="mt-3 border-t border-gray-100 dark:border-gray-800 pt-2">
      <button type="button" onClick={() => setOpen((v) => !v)} className="text-xs text-accent hover:underline">
        {open ? 'Hide' : 'Edit'} the rules' wording and extra rules
      </button>
      {open ? (
        <div className="mt-2 space-y-3">
          <div className="flex gap-2 text-xs">
            {(['default', 'project'] as const).map((s) => (
              <button key={s} type="button" onClick={() => setScope(s)}
                className={`px-2 py-0.5 rounded border ${scope === s ? 'border-accent text-accent bg-accent/10' : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300'}`}>
                {s === 'default' ? 'All projects' : 'This project (overrides)'}
              </button>
            ))}
          </div>
          {KEYS.map(({ key, label }) => (
            <div key={key}>
              <label className="block text-xs text-gray-600 dark:text-gray-300 mb-0.5">{label}</label>
              <textarea rows={3} maxLength={1500} value={texts[key] || ''} className={field}
                onChange={(e) => setTexts((t) => ({ ...t, [key]: e.target.value }))}
                placeholder="Empty resets it to the built-in wording" />
            </div>
          ))}
          <div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-600 dark:text-gray-300">Extra rules {scope === 'project' ? '(replace the default list here)' : ''}</span>
              <button type="button" disabled={extra.length >= 10}
                onClick={() => setExtra((x) => [...x, { id: '', text: '', level: 'recommend' }])}
                className="text-xs text-accent disabled:opacity-40">+ Add a rule</button>
            </div>
            <div className="space-y-2 mt-1">
              {extra.map((x, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <textarea rows={2} maxLength={1500} value={x.text} className={field}
                    onChange={(e) => setExtra((xs) => xs.map((y, j) => (j === i ? { ...y, text: e.target.value } : y)))} />
                  <select value={x.level} className="px-1 py-1 text-xs rounded border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                    onChange={(e) => setExtra((xs) => xs.map((y, j) => (j === i ? { ...y, level: e.target.value as WaysExtraRule['level'] } : y)))}>
                    {['off', 'recommend', 'enforce'].map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <button type="button" onClick={() => setExtra((xs) => xs.filter((_, j) => j !== i))}
                    className="text-xs text-gray-400 hover:text-red-500">Remove</button>
                </div>
              ))}
              {extra.length === 0 ? <p className="text-xs text-gray-400">No extra rules.</p> : null}
            </div>
          </div>
          <button type="button" disabled={saving} onClick={save}
            className="px-3 py-1 text-sm rounded-md bg-accent text-white disabled:opacity-50">
            {saving ? 'Saving…' : `Save for ${scope === 'default' ? 'all projects' : 'this project'}`}
          </button>
        </div>
      ) : null}
    </div>
  );
}
